import moment from "moment";
import { formatPhoneNumber } from "../../utils/formatPhoneNumber";
import type { ColDef, ValueFormatterParams } from "ag-grid-community";

export const callTableColumnDefs: ColDef[] = [
  {
    field: "firstName",
    width: 200,
    cellDataType: "text",
    filter: "agTextColumnFilter",
    headerName: "First Name",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? `${params.value}` : "N/A"
  },
  {
    field: "emailAddress",
    width: 300,
    cellDataType: "text",
    filter: "agTextColumnFilter",
    headerName: "Email",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? `${params.value}` : "N/A"
  },
  {
    field: "phoneNumber",
    width: 200,
    cellDataType: "text",
    filter: "agTextColumnFilter",
    headerName: "Phone #",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? formatPhoneNumber(params.value) : ""
  },
  {
    field: "callStatus",
    cellDataType: "text",
    width: 300,
    filter: "agTextColumnFilter",
    headerName: "Call Status",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value ? `${params.value}` : "N/A";
    }
  },
  {
    field: "vendor",
    cellDataType: "text",
    width: 300,
    filter: "agTextColumnFilter",
    headerName: "Vendor",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value ? `${params.value}` : "N/A";
    }
  },
  {
    field: "start",
    width: 200,
    filter: "agDateColumnFilter",
    headerName: "Start",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value
        ? `${moment(params.value).format("YYYY-MM-DD hh:mm a")}`
        : "N/A";
    },
    filterParams: {
      comparator: (filterDate: string, cellValue: string) => {
        if (!cellValue) return -1;

        const cellDate = moment(cellValue).startOf("day");
        const filterMoment = moment(filterDate).startOf("day");

        if (cellDate.isBefore(filterMoment)) return -1;
        if (cellDate.isAfter(filterMoment)) return 1;
        return 0;
      },
      browserDatePicker: true
    }
  },
  {
    field: "end",
    width: 200,
    filter: "agDateColumnFilter",
    headerName: "End",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value
        ? `${moment(params.value).format("YYYY-MM-DD hh:mm a")}`
        : "N/A";
    },
    filterParams: {
      comparator: (filterDate: string, cellValue: string) => {
        if (!cellValue) return -1;

        const cellDate = moment(cellValue).startOf("day");
        const filterMoment = moment(filterDate).startOf("day");

        if (cellDate.isBefore(filterMoment)) return -1;
        if (cellDate.isAfter(filterMoment)) return 1;
        return 0;
      },
      browserDatePicker: true
    }
  },
  {
    field: "duration",
    width: 150,
    headerName: "Duration",
    floatingFilter: false,
    valueFormatter: (params: ValueFormatterParams) => {
      if (!params.value || Number.isNaN(params.value)) return "N/A";
      const durationInSeconds = Math.floor(params.value / 1000);
      return moment.utc(durationInSeconds * 1000).format("HH:mm:ss");
    }
  },
  {
    field: "disconnectionReason",
    cellDataType: "text",
    width: 300,
    filter: "agTextColumnFilter",
    headerName: "Disconnection Reason",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value ? `${params.value}` : "N/A";
    }
  },
  {
    field: "sentiment",
    cellDataType: "text",
    filter: "agTextColumnFilter",
    headerName: "Sentiment",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value ? `${params.value}` : "N/A";
    }
  },
  {
    field: "callSummary",
    cellDataType: "text",
    width: 300,
    filter: "agTextColumnFilter",
    headerName: "Call Summary",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value ? `${params.value}` : "N/A";
    }
  },
  {
    field: "recordingUrl",
    width: 200,
    cellDataType: "text",
    filter: "agTextColumnFilter",
    headerName: "Recording URL",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value ? `${params.value}` : "N/A";
    }
  },
  {
    field: "action",
    cellDataType: "text",
    filter: "agTextColumnFilter",
    headerName: "Action",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value ? `${params.value}` : "N/A";
    }
  },
  {
    field: "actionTimestamp",
    width: 200,
    filter: "agDateColumnFilter",
    headerName: "Action Timestamp",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value
        ? `${moment(params.value).format("YYYY-MM-DD hh:mm a")}`
        : "N/A";
    },
    filterParams: {
      comparator: (filterDate: string, cellValue: string) => {
        if (!cellValue) return -1;

        const cellDate = moment(cellValue).startOf("day");
        const filterMoment = moment(filterDate).startOf("day");

        if (cellDate.isBefore(filterMoment)) return -1;
        if (cellDate.isAfter(filterMoment)) return 1;
        return 0;
      },
      browserDatePicker: true
    }
  },
  {
    field: "createdAt",
    width: 200,
    filter: "agDateColumnFilter",
    headerName: "Created At",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value
        ? `${moment(params.value).format("YYYY-MM-DD hh:mm a")}`
        : "N/A";
    },
    filterParams: {
      comparator: (filterDate: string, cellValue: string) => {
        if (!cellValue) return -1;

        const cellDate = moment(cellValue).startOf("day");
        const filterMoment = moment(filterDate).startOf("day");

        if (cellDate.isBefore(filterMoment)) return -1;
        if (cellDate.isAfter(filterMoment)) return 1;
        return 0;
      },
      browserDatePicker: true
    }
  },
  {
    field: "updatedAt",
    width: 200,
    sort: "desc",
    filter: "agDateColumnFilter",
    headerName: "Updated At",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value
        ? `${moment(params.value).format("YYYY-MM-DD hh:mm a")}`
        : "N/A";
    },
    filterParams: {
      comparator: (filterDate: string, cellValue: string) => {
        if (!cellValue) return -1;

        const cellDate = moment(cellValue).startOf("day");
        const filterMoment = moment(filterDate).startOf("day");

        if (cellDate.isBefore(filterMoment)) return -1;
        if (cellDate.isAfter(filterMoment)) return 1;
        return 0;
      },
      browserDatePicker: true
    }
  }
];
